import React, { useEffect, useState, useCallback } from 'react';
import './MessagesPage.css';
import MessageDetail from "./MessageDetail/MessageDetail.js"
import { formatTimestamp } from '../../Utils/Utils.js';
import { useSelector } from 'react-redux';
import { FaEnvelope, FaPaperPlane, FaSearch, FaFilter, FaEye, FaClock, FaUser, FaTimes } from 'react-icons/fa';
import { apiUrl, jsonAuthHeaders } from '../../config/api.js';
import { devLog } from '../../config/devLog.js';


export default function MessagesPage() {
  const [activeTab, setActiveTab] = useState('received');
  const [messagesReceived, setMessagesReceived] = useState([]);
  const [messagesSent, setMessagesSent] = useState([]);
  const [postLoading, setPostLoading] = useState(true);
  const [postDetail, setPostDetail] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { userId } = useSelector((state) => state.userReducer);

  const [showModal, setShowModal] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);


  const getPost = useCallback(async (post) => {
    try {
      setPostLoading(true);
      const response = await fetch(apiUrl("getListings?" + post));
      const postResponse = await response.json();
      setPostDetail(postResponse[0]);
    } catch (error) {
      devLog("Error fetching post:", error?.message);
    } finally {
      setPostLoading(false);
    }
  }, []);

  const getInterests = useCallback(async () => {
    try {
      setIsLoading(true);
      if (
        userId == null ||
        userId === "" ||
        String(userId) === "null" ||
        String(userId) === "undefined"
      ) {
        setIsLoading(false);
        return;
      }
      const res = await fetch(apiUrl("getInterests?" + userId), {
        headers: jsonAuthHeaders(),
      });
      const responses = await res.json();
      
      let received = [];
      let sent = [];
      
      if (!responses.message && responses.length > 0) {
        responses.forEach((interest) => {
          if (interest.sender_id === userId) {
            sent.push({
              id: interest.id,
              listingId: interest.listing_id,
              senderId: interest.sender_id,
              hostId: interest.host_id,
              type: "sent",
              name: interest.first_name + " " + interest.last_name,
              contact: interest.mobile_number,
              email: interest.email,
              postTitle: interest.listing_title,
              postImage: interest.image_url?.split(",")[0] || "",
              message: interest.message,
              createdAt: interest.created_at,
              seen: 1
            });
          }
          if (interest.host_id === userId) {
            received.push({
              id: interest.id,
              type: "received",
              listingId: interest.listing_id,
              senderId: interest.sender_id,
              hostId: interest.host_id,
              name: interest.first_name + " " + interest.last_name,
              contact: interest.mobile_number,
              email: interest.email,
              postTitle: interest.listing_title || interest.listing_category + " items for Sale",
              postImage: interest.image_url?.split(",")[0] || "",
              message: interest.message,
              createdAt: interest.created_at,
              seen: interest.seen
            });
          }
        });
        
        if (received.length > 0) {
          getPost(received[0].listingId);
        }
        
        setMessagesReceived([...received]);
        setMessagesSent([...sent]);
      }
    } catch (error) {
      devLog("Error fetching interests:", error?.message);
    } finally {
      setIsLoading(false);
    }
  }, [userId, getPost]);

  useEffect(() => {
    getInterests();
  }, [getInterests]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentMessages = activeTab === 'received' ? messagesReceived : messagesSent;
  const filteredMessages = currentMessages.filter(msg => 
    msg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    msg.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
    msg.postTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

    const updateInterest = async(message) =>{
    if(message.type === "received"){
      const updatedMessages = messagesReceived.map((oldMessage)=>{
        if(oldMessage.id === message.id){
          return {...message, seen:1}

        }
        return oldMessage
      });
      setMessagesReceived(updatedMessages)
      const res= await fetch(apiUrl("updateInterest"),{
        method: "POST",
        headers: jsonAuthHeaders(),
        body: JSON.stringify({
          listingId: message.listingId,
          senderId: message.senderId,
          hostId: message.hostId
        })
      })
      await res.json();
      devLog("[messages] updateInterest", { ok: res.ok })

    }
  }

  if (isLoading) {
    return (
      <div className="messages-page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-page-container">
      

      {/* Messages Content */}
      <div className="messages-content">
        {/* Messages Sidebar */}
        <div className="messages-sidebar">
          {/* Search and Filter */}
          <div className="messages-controls">
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <button className="filter-btn">
              <FaFilter />
              Filter
            </button>
          </div>

          {/* Tab Switcher */}
          <div className="tab-switcher">
            <button
              className={`tab-btn ${activeTab === 'received' ? 'active' : ''}`}
              onClick={() => setActiveTab('received')}
            >
              <FaEnvelope />
              Received ({messagesReceived.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'sent' ? 'active' : ''}`}
              onClick={() => setActiveTab('sent')}
            >
              <FaPaperPlane />
              Sent ({messagesSent.length})
            </button>
          </div>

          {/* Messages List */}
          <div className="messages-list">
            {filteredMessages.length === 0 ? (
              <div className="empty-state">
                <FaEnvelope className="empty-icon" />
                <h3>No Messages Found</h3>
                <p>You don't have any {activeTab} messages yet.</p>
              </div>
            ) : (
              filteredMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message-card ${selectedMessage.id === msg.id ? 'selected' : ''} ${!msg.seen ? 'unread' : ''}`}
                  onClick={() => {
                    getPost(msg.listingId);
                    updateInterest(msg);
                    setSelectedMessage(msg);
                    if (isMobileView) setShowModal(true);
                  }}
                >
                  <div className="message-avatar">
                    {msg.postImage ? (
                      <img src={msg.postImage} alt={msg.name} />
                    ) : (
                      <div className="avatar-placeholder">
                        <FaUser />
                      </div>
                    )}
                    {!msg.seen && <div className="unread-indicator"></div>}
                  </div>
                  
                  <div className="message-content">
                    <div className="message-header">
                      <h4 className="sender-name">{msg.name}</h4>
                      <span className="message-time">
                        <FaClock />
                        {formatTimestamp(msg.createdAt)}
                      </span>
                    </div>
                    
                    <div className="message-preview">
                      <p className="post-title">{msg.postTitle}</p>
                      <p className="message-text">{msg.message}</p>
                    </div>
                    
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Detail Panel */}
        <div className="message-detail-panel">
          {selectedMessage.id && !isMobileView ? (
            <MessageDetail 
              loading={postLoading} 
              post={postDetail} 
              message={selectedMessage} 
            />
          ) : (
            <div className="no-selection">
              <FaEye className="no-selection-icon" />
              <h3>Select a Message</h3>
              <p>Choose a message from the list to view details</p>
            </div>
          )}

          {isMobileView && showModal && selectedMessage.id && (
            (() => {
              const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
              const modalStyle = { background: '#fff', borderRadius: 8, maxWidth: '95%', width: '600px', maxHeight: '90%', overflowY: 'auto', position: 'relative', padding: 16 };
              const closeStyle = { position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' };
              return (
                <div style={overlayStyle} onClick={() => setShowModal(false)}>
                  <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                    <button style={closeStyle} onClick={() => setShowModal(false)} aria-label="Close">
                      <FaTimes />
                    </button>
                    <MessageDetail loading={postLoading} post={postDetail} message={selectedMessage} />
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
}
